// Centralized helpers for choosing a provider's display photo.
//
// Priority:
// 1. Custom uploaded photo (profile_photo_url || legacy logo_url)
// 2. Manual gender / detected avatar_gender → matching silhouette
// 3. Fallback default avatar (male silhouette)
//
// Default URLs are stored in app_assets but we keep static fallbacks here so
// the UI never breaks if app_assets is not yet seeded.

import maleSilhouette from "@/assets/default-provider-male.png";
import femaleSilhouette from "@/assets/default-provider-female.png";

export const DEFAULT_MALE_AVATAR = maleSilhouette;
export const DEFAULT_FEMALE_AVATAR = femaleSilhouette;
export const DEFAULT_FALLBACK_AVATAR = maleSilhouette;

export type ProviderAvatarFields = {
 profile_photo_url?: string | null;
 display_photo_url?: string | null;
 logo_url?: string | null;
 avatar_gender?: string | null;
 gender?: string | null;
 name?: string | null;
};

function pickByGender(g?: string | null) {
 if (g === "female") return DEFAULT_FEMALE_AVATAR;
 if (g === "male") return DEFAULT_MALE_AVATAR;
 return DEFAULT_FALLBACK_AVATAR;
}

/** Resolve the final image URL to display for a provider. */
export function getProviderDisplayPhoto(p: ProviderAvatarFields): string {
 const custom = (p.profile_photo_url || p.logo_url || "").trim();
 if (custom) return custom;
 if (p.display_photo_url && p.display_photo_url.trim()) return p.display_photo_url;
 const g = p.avatar_gender || p.gender || detectGenderFromName(p.name || "").gender;
 return pickByGender(g);
}

/** Image-element onError handler that swaps to the correct default silhouette. */
export function avatarOnError(p: ProviderAvatarFields) {
 return (e: React.SyntheticEvent<HTMLImageElement>) => {
 const fallback = pickByGender(p.avatar_gender || p.gender);
 const img = e.currentTarget;
 if (img.src !== fallback) img.src = fallback;
 };
}

const TITLE_RX = /\b(dr|doctor|md|do|dds|dmd|np|pa|fnp|rn|phd|prof|professor|sr|jr)\.?\b/gi;

export function extractFirstName(fullName: string): string {
 if (!fullName) return "";
 const cleaned = fullName.replace(TITLE_RX, " ").replace(/[.,]/g, " ").trim();
 const parts = cleaned.split(/\s+/).filter(Boolean);
 return parts[0] || "";
}

export type GenderDetectionResult = {
 gender: "male" | "female" | "unknown";
 confidence: number; // 0-100
 source: "name_detection" | "fallback";
};

/**
 * Detect likely gender from a provider's name. Uses a curated dataset.
 * Confidence is 95 when the name maps cleanly, 0 otherwise.
 * The detected gender is for *internal default-avatar selection only* 
 * never display it to patients.
 */
export function detectGenderFromName(fullName: string): GenderDetectionResult {
 const first = extractFirstName(fullName);
 if (!first) return { gender: "unknown", confidence: 0, source: "fallback" };
 try {
 // CommonJS module, dynamic require keeps it server/SSR safe.
 // eslint-disable-next-line @typescript-eslint/no-var-requires
 const { getGender } = require("gender-detection-from-name") as {
 getGender: (n: string) => "male" | "female" | "unknown";
 };
 const g = getGender(first);
 if (g === "male" || g === "female") {
 return { gender: g, confidence: 95, source: "name_detection" };
 }
 } catch {
 // fall through
 }
 return { gender: "unknown", confidence: 0, source: "fallback" };
}

/**
 * Compute avatar fields for a provider. Use when saving to DB so the
 * stored display_photo_url stays in sync.
 */
export function resolveProviderAvatar(input: {
 name?: string | null;
 profile_photo_url?: string | null;
 gender?: string | null;
 avatar_gender_override?: string | null;
}): {
 display_photo_url: string;
 avatar_gender: "male" | "female" | "unknown";
 avatar_source: "custom_photo" | "manual_gender" | "name_detection" | "fallback";
 avatar_confidence: number;
 has_custom_photo: boolean;
 avatar_needs_review: boolean;
} {
 const custom = (input.profile_photo_url || "").trim();
 if (custom) {
 return {
 display_photo_url: custom,
 avatar_gender: (input.avatar_gender_override as any) || (input.gender as any) || "unknown",
 avatar_source: "custom_photo",
 avatar_confidence: 100,
 has_custom_photo: true,
 avatar_needs_review: false,
 };
 }
 if (input.avatar_gender_override === "male" || input.avatar_gender_override === "female" || input.avatar_gender_override === "unknown") {
 const g = input.avatar_gender_override;
 return {
 display_photo_url: pickByGender(g),
 avatar_gender: g,
 avatar_source: "manual_gender",
 avatar_confidence: 100,
 has_custom_photo: false,
 avatar_needs_review: g === "unknown",
 };
 }
 if (input.gender === "male" || input.gender === "female") {
 return {
 display_photo_url: pickByGender(input.gender),
 avatar_gender: input.gender,
 avatar_source: "manual_gender",
 avatar_confidence: 100,
 has_custom_photo: false,
 avatar_needs_review: false,
 };
 }
 const detected = detectGenderFromName(input.name || "");
 if (detected.confidence >= 85 && detected.gender !== "unknown") {
 return {
 display_photo_url: pickByGender(detected.gender),
 avatar_gender: detected.gender,
 avatar_source: "name_detection",
 avatar_confidence: detected.confidence,
 has_custom_photo: false,
 avatar_needs_review: false,
 };
 }
 return {
 display_photo_url: DEFAULT_FALLBACK_AVATAR,
 avatar_gender: "unknown",
 avatar_source: "fallback",
 avatar_confidence: detected.confidence,
 has_custom_photo: false,
 avatar_needs_review: true,
 };
}
